import { zustandStorage } from "@helpers/storage";
import type { Apps } from "types/apps";
/* -----------------Globals--------------- */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type Country = {
	type: string;
	title: string;
	image: string;
	id?: string | undefined;
};

export type Features = "hint" | "aila" | "haptic" | "notifications" | "alerts";
/* -----------------Types--------------- */
type State = {
	hasShownSummativeTestAlert?: boolean;
	hasShownConfidenceOnboardingDialog?: boolean;
	hasShownMasteryTopicsAlert?: boolean;
	lastLoggedInEmail?: string;
	showAvatarUpdatedMessage?: boolean;
	language?: string;
	country?: Country;
	showAudioPlaybackTestDialog: boolean;
	isPremiumUser?: boolean;
	hasShownChallengeInstructions?: boolean;
	selectedApp?: Apps;
	setSelectedApp: (app: Apps) => void;
	setIsPremiumUser: (isPremiumUser: boolean) => void;
	setLastLoggedInEmail: (email: string) => void;
	setHasShownMasteryTopicsAlert: (hasShown: boolean) => void;
	setHasShownSummativeTestAlert: (hasShown: boolean) => void;
	setHasShownConfidenceOnboardingDialog: (hasShown: boolean) => void;
	setShowAvatarUpdatedMessage: (hasShown: boolean) => void;
	setLanguage: (lang: string) => void;
	toggleAudioPlaybackTestDialog: () => void;
	setCountry: (country: Country) => void;
	settingsEnabledFeatures: Record<Features, boolean>;
	toggleFeature: (feature: Features) => void;
	setHasShownChallengeInstructions: (hasShown: boolean) => void;
};

/* -----------------Store--------------- */
export const useGlobalStore = create<State>()(
	persist(
		(set) => ({
			isPremiumUser: false,
			setIsPremiumUser: (isPremiumUser) => set({ isPremiumUser }),
			showAudioPlaybackTestDialog: true,
			selectedApp: undefined,
			setSelectedApp: (app) => set({ selectedApp: app }),
			setHasShownChallengeInstructions: (hasShown) =>
				set({ hasShownChallengeInstructions: hasShown }),
			setCountry: (country) => set({ country }),
			setHasShownMasteryTopicsAlert: (hasShown) =>
				set({ hasShownMasteryTopicsAlert: hasShown }),
			setHasShownSummativeTestAlert: (hasShown) =>
				set({ hasShownSummativeTestAlert: hasShown }),
			setHasShownConfidenceOnboardingDialog: (hasShown) =>
				set({ hasShownConfidenceOnboardingDialog: hasShown }),
			settingsEnabledFeatures: {
				hint: true,
				aila: true,
				haptic: true,
				notifications: true,
				alerts: true,
			},
			toggleFeature: (feature) =>
				set((state) => ({
					settingsEnabledFeatures: {
						...state.settingsEnabledFeatures,
						[feature]: !state.settingsEnabledFeatures[feature],
					},
				})),
			setLastLoggedInEmail: (email) => set({ lastLoggedInEmail: email }),
			setShowAvatarUpdatedMessage: (hasShown) =>
				set({ showAvatarUpdatedMessage: hasShown }),
			setLanguage: (lang) => set({ language: lang }),
			toggleAudioPlaybackTestDialog: () =>
				set((state) => ({
					showAudioPlaybackTestDialog: !state.showAudioPlaybackTestDialog,
				})),
		}),

		{
			name: "global-store",
			storage: createJSONStorage(() => zustandStorage),
		},
	),
);
